import { useState } from "react";
import MaterialIcon from "../../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import styles from "./site-location-card.module.scss";

export interface ISiteLocation {
  locationName: string;
  latitude: number | string;
  longitude: number | string;
  url: string | null;
}

interface IProps {
  address: ISiteLocation | null;
}

const toCoordinate = (value: number | string | null | undefined) => {
  const parsed = Number(value);
  return value !== null && value !== "" && Number.isFinite(parsed)
    ? parsed
    : null;
};

const SiteLocationCard = ({ address }: IProps) => {
  const [isCopied, setIsCopied] = useState(false);

  const latitude = toCoordinate(address?.latitude);
  const longitude = toCoordinate(address?.longitude);
  const hasCoordinates = latitude !== null && longitude !== null;
  const locationName = address?.locationName?.trim();

  if (!locationName && !hasCoordinates) {
    return (
      <div className={`${styles.card} ${styles.empty}`}>
        <MaterialIcon
          icon="location_on"
          size={20}
          color="var(--color-text-color-2)"
        />
        <div>
          <div className="text-label font-weight-500 font-size-14">
            Site Location
          </div>
          <div className="text-advance-description font-size-12">
            The customer has not provided an installation address yet.
          </div>
        </div>
      </div>
    );
  }

  const coordinatesText = hasCoordinates
    ? `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
    : "";
  const mapQuery = hasCoordinates
    ? `${latitude},${longitude}`
    : encodeURIComponent(locationName ?? "");
  const mapsUrl =
    address?.url ||
    `https://www.google.com/maps/search/?api=1&query=${mapQuery}`;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(
        [locationName, coordinatesText].filter(Boolean).join("\n"),
      );
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      // Clipboard can be unavailable (e.g. non-secure context); ignore silently
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <MaterialIcon
          icon="location_on"
          size={20}
          color="var(--color-a-href)"
        />
        <span className="text-label font-weight-600 font-size-14">
          Site Location
        </span>
      </div>

      {locationName && (
        <div className="text-label font-weight-500 font-size-14 mt-2">
          {locationName}
        </div>
      )}
      {hasCoordinates && (
        <div className="text-advance-description font-size-12 mt-1">
          {coordinatesText}
        </div>
      )}

      <iframe
        title="Site location map"
        className={styles.map}
        src={`https://maps.google.com/maps?q=${mapQuery}&z=15&output=embed`}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />

      <div className={styles.actions}>
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.action}
        >
          <MaterialIcon
            icon="open_in_new"
            size={16}
            color="var(--color-a-href)"
          />
          Open in Google Maps
        </a>
        <button type="button" className={styles.action} onClick={onCopy}>
          {isCopied && (
            <MaterialIcon icon="check" size={16} color="var(--color-a-href)" />
          )}
          {isCopied ? "Copied" : "Copy address"}
        </button>
      </div>
    </div>
  );
};

export default SiteLocationCard;
